import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

/**
 * LC 누적 테스트 수학머리 — BIGGO World 매핑 (superadmin).
 * 스코프(커리큘럼 + 첫 챕터 content) → BIGGO World Key. 시드 후 superadmin이 유지한다.
 */

export interface MathHeadScopeChapter {
  contentId: number
  ordinal: number
  /** 코드 접두어 포함 제목([Ch-01-01] …) — superadmin 화면이라 그대로 노출 */
  title: string
}

export interface MathHeadWorldMappingScope {
  scopeStartChapterContentId: number
  scopeEndChapterContentId: number
  scopeStartChapterOrdinal: number
  scopeEndChapterOrdinal: number
  placementChapterOrdinal: number
  chapters: MathHeadScopeChapter[]
  /** null = 매핑 없음(학습자에게는 시작 불가 → 교사 면제 경로) */
  worldKey: string | null
  updatedByUserId: number | null
  updatedAt: string | null
}

export interface MathHeadWorldMappingOrphan {
  scopeStartChapterContentId: number
  scopeEndChapterContentId: number
  worldKey: string
}

export interface MathHeadWorldMappingList {
  curriculumContentId: number
  curriculumTitle: string
  scopes: MathHeadWorldMappingScope[]
  /** 커리큘럼 트리 변경으로 더 이상 스코프 시작이 아닌 챕터에 남은 매핑 */
  orphanMappings: MathHeadWorldMappingOrphan[]
}

export interface MathHeadWorldMappingUpsertResult {
  curriculumContentId: number
  scopeStartChapterContentId: number
  scopeEndChapterContentId: number
  /** null = 삭제됨 */
  worldKey: string | null
}

export const WORLD_KEY_PATTERN = /^[A-Za-z0-9_-]{1,64}$/

const BASE = '/admin/lc/math-head/world-mappings'

export const mathHeadWorldMappingQueryKeys = {
  all: ['admin', 'lc', 'math-head-world-mappings'] as const,
  list: (curriculumId: number) => ['admin', 'lc', 'math-head-world-mappings', curriculumId] as const,
}

export const mathHeadWorldMappingApi = {
  list: async (curriculumId: number): Promise<MathHeadWorldMappingList> => {
    const res = await apiClient.get<ApiResponse<MathHeadWorldMappingList>>(BASE, { params: { curriculumId } })
    const data = res.data.data
    if (!data) throw new Error('World 매핑 응답이 비어 있습니다.')
    return data
  },

  /** 빈 문자열이면 매핑 삭제 */
  upsert: async (
    curriculumId: number,
    scopeStartChapterContentId: number,
    worldKey: string,
  ): Promise<MathHeadWorldMappingUpsertResult> => {
    const res = await apiClient.put<ApiResponse<MathHeadWorldMappingUpsertResult>>(
      `${BASE}/${curriculumId}/${scopeStartChapterContentId}`,
      { worldKey: worldKey.trim() },
    )
    const data = res.data.data
    if (!data) throw new Error('World 매핑 저장 응답이 비어 있습니다.')
    return data
  },
}
